import type { CSSProperties } from 'react';

import { cn } from '@heidi/utils';

import type { PanelToggleSide } from './panel-toggle-button.types';

/**
 * Closed-state scale of the inner bar. Matches the sidebar pin glyph, whose
 * bar shrinks from a 6px "open" width to a 2px "closed" marker (2 / 6).
 */
const CLOSED_SCALE = 1 / 3;

/**
 * Per-side geometry for the inner bar. Each bar is drawn at its full ("open")
 * size, anchored to the relevant edge, then scaled down toward that edge when
 * closed via a transform — keeping the animation on a GPU-friendly transform
 * rather than animating SVG geometry attributes (which Firefox tweens poorly).
 */
const BAR_BY_SIDE: Record<
  PanelToggleSide,
  {
    x: number;
    y: number;
    width: number;
    height: number;
    transformOrigin: string;
    axis: 'x' | 'y';
  }
> = {
  left: { x: 6, y: 6, width: 6, height: 12, transformOrigin: '0% 50%', axis: 'x' },
  right: { x: 12, y: 6, width: 6, height: 12, transformOrigin: '100% 50%', axis: 'x' },
  bottom: { x: 6, y: 12, width: 12, height: 6, transformOrigin: '50% 100%', axis: 'y' },
};

interface PanelToggleIconProps {
  open: boolean;
  side: PanelToggleSide;
  className?: string;
}

/**
 * Panel-toggle glyph: a rounded square outline with an inner filled bar
 * anchored to one edge. The bar grows toward that edge when `open` and shrinks
 * to a thin marker when closed — the orientation-flippable inverse of the
 * sidebar pin icon. Sizing is inherited from the parent Button's automatic SVG
 * sizing unless overridden via `className`.
 */
export function PanelToggleIcon({ open, side, className }: PanelToggleIconProps) {
  const bar = BAR_BY_SIDE[side];
  const scale = open ? 1 : CLOSED_SCALE;
  const barStyle: CSSProperties = {
    transformBox: 'fill-box',
    transformOrigin: bar.transformOrigin,
    transform: bar.axis === 'x' ? `scaleX(${scale})` : `scaleY(${scale})`,
  };

  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn(
        'shrink-0',
        // `side` is a *physical* edge, but a horizontal panel mirrors in RTL — a
        // right-docked inline panel sits on the left under `dir=rtl`, so the glyph
        // (and its edge-anchored bar) must flip to point at the correct side. The
        // `bottom` orientation is vertical and unaffected, so only flip the x-axis
        // sides. `-scale-x-100` composes with the bar's own scaleX/scaleY animation.
        bar.axis === 'x' && 'rtl:-scale-x-100',
        className
      )}
      aria-hidden="true"
    >
      {/* Outer border */}
      <rect width="18" height="18" x="3" y="3" rx="3" />
      {/* Inner bar — grows toward the panel edge when open, shrinks when closed */}
      <rect
        x={bar.x}
        y={bar.y}
        width={bar.width}
        height={bar.height}
        rx="1.5"
        fill="currentColor"
        stroke="none"
        className="motion-safe:transition-transform motion-safe:duration-200 motion-safe:ease-out"
        style={barStyle}
      />
    </svg>
  );
}
